/**
 * @file        src/pages/staff/AddStaff.jsx
 * @description Create / edit a staff member: basic info (first/last name,
 *              email, mobile, password + confirm) and Software Access (module
 *              multi-select). The EMAIL is the login. Password required on
 *              create, optional on edit (blank = keep). Reused for /staff/new
 *              and /staff/:id/edit.
 * @author      Kinetika Labs
 * @created     2026-07-03
 * @changelog
 *   - 2026-09-16 Claude — Login ID field removed (migration 063 drops the
 *                          column). A staff member signs in with their email
 *                          alone; the 8–12-character ID was a second credential
 *                          that identified nothing the email did not, and had to
 *                          be invented and remembered for every staff member.
 *                          The server-side EMAIL_TAKEN mapping stays and now
 *                          matters more: the address is checked against EVERY
 *                          identity store (emailIdentity.service), not just other
 *                          staff rows, because an address already held by an
 *                          admin-console login would shadow the staff row at
 *                          sign-in.
 *   - 2026-09-16 Claude — Password fields carry autoComplete="new-password" +
 *                          data-lpignore / data-1p-ignore, so the browser and
 *                          password managers stop pre-filling the signed-in
 *                          admin's own password into them. Only Login.jsx keeps
 *                          "current-password".
 *   - 2026-09-15 Claude — Corporate Flight/Hotel Management are assignable
 *                          modules of their own (see MODULES below). Existing
 *                          staff keep exactly what they had: nobody is granted
 *                          the new keys automatically, so corporate data becomes
 *                          invisible to them until a tenant_admin ticks it —
 *                          which is the intended direction for a permission fix.
 *   - 2026-07-03 Claude — Initial (Staff Management).
 *   - 2026-08-03 Claude — Added "Wallet Management" to MODULES — the wallet
 *                          screens are now their own sidebar group (nav.js).
 *   - 2026-08-04 Claude — Staff can now actually log in and these checkboxes
 *                          are enforced server-side (middleware/requireModule).
 *                          Added "FAQ Management" (nav.js gained it 08-04, now
 *                          gated). REMOVED "Staff Management" — granting it
 *                          would let a staff account manage other staff,
 *                          including granting itself more modules; the backend
 *                          deliberately never honors it for that reason (see
 *                          routes/staff.routes.js), so offering the checkbox
 *                          here would just lie to whoever checks it. B2C
 *                          Flight/Hotel Management also cover the Corporate
 *                          Flight/Hotel screens — same backend routes, filtered
 *                          by an audience param, not a separate module.
 *   - 2026-08-10 Claude — Added mobile (10-digit), name, and login-ID format
 *                          validation to match the backend (staff.routes.js).
 *                          Duplicate-email/login-ID 409s from the server
 *                          (staff.service.js ensureEmailFree/ensureLoginIdFree,
 *                          checked against existing staff rows) now surface
 *                          under the offending field instead of a bare toast.
 *   - 2026-09-17 Claude — Password + confirm are now <PasswordInput>
 *                          (reveal toggle).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import { get, post, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import PasswordInput from '../../components/PasswordInput';

// Software-access modules — each is enforced server-side by
// middleware/requireModule.js on the matching API routes. 'Staff Management' is
// deliberately NOT offered here (see changelog) — only a tenant_admin can
// manage staff, regardless of what's checked below.
const MODULES = [
  'Account Management',
  'Airport Management',
  'API Management',
  'B2C Flight Management',
  'B2C Hotel Management',
  // Corporate Flight/Hotel are SEPARATE sidebar sections with separate data
  // (CLAUDE.md §9d), so they are separate grants. They used to ride on the B2C
  // keys above — one map entry in Sidebar.jsx and one requireModule on routes
  // that serve both — which meant a retail grant silently opened every
  // corporate booking, markup, coupon and cancellation.
  'Corporate Flight Management',
  'Corporate Hotel Management',
  // Travel insurance is a third product with the same B2C/Corporate split, and
  // the same reason for separate grants (2026-09-22).
  'B2C Insurance Management',
  'Corporate Insurance Management',
  'Blog Management',
  'Customer Management',
  'FAQ Management',
  'Marketing Management',
  'Offer Management',
  'Package Management',
  'Payment Management',
  'Query Management',
  'Site Management',
  'Testimonial Management',
  'Wallet Management',
];

const NAME_RE = /^[A-Za-z][A-Za-z\s'-]*$/;
const MOBILE_RE = /^[6-9]\d{9}$/;

const baseSchema = {
  first_name: z.string().trim().min(1, 'Required').max(100)
    .regex(NAME_RE, 'Letters only'),
  last_name: z.union([z.string().trim().max(100).regex(NAME_RE, 'Letters only'), z.literal('')]).optional(),
  email: z.string().trim().email('Invalid email').max(190),
  mobile: z.union([z.string().trim().regex(MOBILE_RE, 'Enter a valid 10-digit mobile number'), z.literal('')]).optional(),
  is_active: z.boolean(),
};

const empty = {
  first_name: '', last_name: '', email: '', mobile: '',
  password: '', confirm: '', modules: [], is_active: true,
};

const STATUS = [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }];

export default function AddStaff() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const schema = z.object({
    ...baseSchema,
    password: isEdit
      ? z.union([z.string().min(6, 'Min 6 chars'), z.literal('')]).optional()
      : z.string().min(6, 'Min 6 chars'),
  });

  const { data: existing } = useQuery({
    queryKey: qk.staff.one(id),
    queryFn: () => get(`/staff/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing?.data) {
      const s = existing.data;
      setForm({
        first_name: s.first_name || '', last_name: s.last_name || '', email: s.email || '',
        mobile: s.mobile || '', password: '', confirm: '',
        modules: Array.isArray(s.modules) ? s.modules : [], is_active: !!s.is_active,
      });
    }
  }, [existing]);

  const mut = useMutation({
    mutationFn: (b) => (isEdit ? patch(`/staff/${id}`, b) : post('/staff', b)),
    onSuccess: () => {
      toast.ok(isEdit ? 'Staff updated' : 'Staff registered', `${form.first_name} saved.`);
      qc.invalidateQueries({ queryKey: ['staff'] });
      navigate('/staff');
    },
    onError: (err) => {
      const apiErr = err.response?.data?.error;
      const f = apiErr?.fields ? { ...apiErr.fields } : {};
      // A duplicate login email comes back as a 409 with a code, not a
      // per-field validation error — map it here so it lands under the input
      // instead of only a toast. The server checks the address against every
      // identity store (staff, customers, corporate accounts and the platform
      // admins), so the message names which one already holds it.
      if (apiErr?.code === 'EMAIL_TAKEN') f.email = apiErr.message;
      if (Object.keys(f).length) setErrors(f);
      toast.err('Could not save', apiErr?.message || 'Validation failed');
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    const fe = {};
    if (!parsed.success) {
      for (const i of parsed.error.issues) fe[i.path.join('.')] = i.message;
    }
    // Confirm-password check (matches the legacy form) — required whenever a
    // password is being set.
    if (form.password && form.password !== form.confirm) fe.confirm = 'Passwords do not match';
    if (!isEdit && !form.confirm) fe.confirm = fe.confirm || 'Required';
    if (Object.keys(fe).length) return setErrors(fe);

    setErrors({});
    const d = parsed.data;
    const body = {
      first_name: d.first_name, email: d.email,
      modules: form.modules, is_active: d.is_active,
    };
    for (const k of ['last_name', 'mobile']) if (d[k]) body[k] = d[k];
    if (form.password) body.password = form.password;
    mut.mutate(body);
  }

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleModule = (m) =>
    setForm((f) => ({
      ...f,
      modules: f.modules.includes(m) ? f.modules.filter((x) => x !== m) : [...f.modules, m],
    }));
  const allSelected = form.modules.length === MODULES.length;

  return (
    <>
      <PageHead
        title={isEdit ? 'Edit Staff' : 'Add New Staff'}
        subtitle="Staff Management"
        actions={<button className="btn ghost" onClick={() => navigate('/staff')}><Icon name="x" /> Cancel</button>}
      />

      <article className="panel glass">
        <h3 className="panel-subhead">Staff Basic Information</h3>
        <form className="form-grid" onSubmit={onSubmit}>
          <Field label="First Name" required err={errors.first_name}>
            <input value={form.first_name} onChange={on('first_name')} placeholder="Enter staff first name" />
          </Field>
          <Field label="Last Name" err={errors.last_name}>
            <input value={form.last_name} onChange={on('last_name')} placeholder="Enter staff last name" />
          </Field>
          {/* The staff login. Must be unique across every identity store — the
              server answers 409 EMAIL_TAKEN naming the holder. */}
          <Field label="Email" required err={errors.email} hint="This is the staff member's login.">
            <input value={form.email} onChange={on('email')} placeholder="Email ID" />
          </Field>

          <Field label="Mobile" err={errors.mobile}>
            <input
              value={form.mobile}
              onChange={on('mobile')}
              placeholder="10-digit mobile number"
              inputMode="numeric"
              maxLength={10}
            />
          </Field>
          <Field label="Password" required={!isEdit} err={errors.password}
                 hint={isEdit ? 'Leave blank to keep current password.' : 'Min 6 characters.'}>
            <PasswordInput value={form.password} onChange={on('password')} placeholder="Login password" autoComplete="new-password" data-lpignore="true" data-1p-ignore />
          </Field>
          <Field label="Confirm Password" required={!isEdit || !!form.password} err={errors.confirm}>
            <PasswordInput value={form.confirm} onChange={on('confirm')} placeholder="Confirm password" autoComplete="new-password" data-lpignore="true" data-1p-ignore />
          </Field>

          <Field label="Status">
            <Select
              value={form.is_active ? 'active' : 'inactive'}
              onChange={(v) => setForm({ ...form, is_active: v === 'active' })}
              options={STATUS}
            />
          </Field>

          <div className="field full">
            <label>
              Software Access — Modules{' '}
              <button
                type="button"
                className="link-btn"
                onClick={() => setForm({ ...form, modules: allSelected ? [] : [...MODULES] })}
              >
                {allSelected ? 'Clear all' : 'Select all'}
              </button>
            </label>
            <div className="mod-grid">
              {MODULES.map((m) => (
                <label key={m} className={`mod-chip${form.modules.includes(m) ? ' on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={form.modules.includes(m)}
                    onChange={() => toggleModule(m)}
                  />
                  {m}
                </label>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <button className="btn primary" type="submit" disabled={mut.isPending}>
              <Icon name="plus" /> {mut.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Register'}
            </button>
            <button className="btn ghost" type="button" onClick={() => { setForm(empty); setErrors({}); }}>
              Reset
            </button>
          </div>
        </form>
      </article>
    </>
  );
}

function Field({ label, required, err, hint, full, children }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label>{label} {required ? <span className="req">*</span> : null}</label>
      {children}
      {hint && !err ? <div className="field-hint muted">{hint}</div> : null}
      {err ? <div className="field-error">{err}</div> : null}
    </div>
  );
}
